import React, { useEffect, useState } from "react";
import { X, Plus, FileText, CheckCircle2, Clock, Edit3, Trash2, Filter } from "lucide-react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { InvoiceItem, getStudentInvoices, deleteInvoice } from "../../services/admin";
import { InvoiceModal } from "./InvoiceModal";
import { generateAcademicSessions } from "../../utilities/academicSession";

interface StudentInvoiceDrawerProps {
  studentId: string;
  studentName: string;
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

export const StudentInvoiceDrawer: React.FC<StudentInvoiceDrawerProps> = ({
  studentId,
  studentName,
  isOpen,
  onClose,
  isAdmin = true,
}) => {
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filters
  const [sessionFilter, setSessionFilter] = useState<string>("2025/2026");
  const [termFilter, setTermFilter] = useState<string>("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceItem | null>(null);

  const fetchInvoices = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await getStudentInvoices({
        student_id: studentId,
        academic_session: sessionFilter || undefined,
        term: termFilter || undefined,
      });
      if (res?.data && Array.isArray(res.data)) {
        setInvoices(res.data);
      } else if (Array.isArray(res)) {
        setInvoices(res);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Failed to fetch student invoices:", error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = (invoice: InvoiceItem) => {
    if (Number(invoice.amount_paid || 0) > 0) {
      toast.error("Cannot delete invoice because payments have already been received.");
      return;
    }

    Swal.fire({
      title: "Delete Invoice?",
      text: `Are you sure you want to delete invoice "${invoice.invoice_code || invoice.id}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteInvoice(invoice.id);
          toast.success("Invoice deleted successfully.");
          fetchInvoices();
        } catch (error: any) {
          console.error("Failed to delete invoice:", error);
          toast.error(error?.response?.data?.message || "Failed to delete invoice.");
        }
      }
    });
  };

  useEffect(() => {
    if (isOpen) {
      fetchInvoices();
    }
  }, [isOpen, studentId, sessionFilter, termFilter]);

  if (!isOpen) return null;

  const getStatusBadge = (status?: string, remaining?: number) => {
    if (status === "paid" || remaining === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-700">
          <CheckCircle2 size={13} /> Paid
        </span>
      );
    }
    if (status === "partially_paid") {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black bg-amber-100 text-amber-700">
          <Clock size={13} /> Partially Paid
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black bg-blue-100 text-blue-700">
        <FileText size={13} /> Issued
      </span>
    );
  };

  const sessions = generateAcademicSessions(2025);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-end animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-xl h-full shadow-2xl flex flex-col relative animate-in slide-in-from-right duration-300">
        {/* Drawer Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center font-bold shadow-md shadow-blue-100">
              <FileText size={22} />
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-800 leading-tight">
                {studentName}
              </h3>
              <p className="text-slate-400 font-bold text-xs">
                Student Fee Invoices & History
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  setEditingInvoice(null);
                  setIsModalOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider flex items-center gap-1.5 shadow-md shadow-blue-100"
              >
                <Plus size={16} /> New Invoice
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-slate-200/60 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-all"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 bg-slate-100/70 border-b border-slate-200/60 flex items-center gap-3">
          <Filter size={15} className="text-slate-400 shrink-0" />
          <select
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-700 outline-none"
          >
            <option value="">All Sessions</option>
            {sessions.map(({ value: s }) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            value={termFilter}
            onChange={(e) => setTermFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-700 outline-none"
          >
            <option value="">All Terms</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
            <option value="3">Term 3</option>
          </select>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-slate-400 font-bold text-xs">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="bg-slate-50 rounded-3xl p-10 text-center border-2 border-dashed border-slate-200 flex flex-col items-center justify-center my-8">
              <FileText className="text-slate-300 mb-3" size={40} />
              <h4 className="text-base font-black text-slate-700 mb-1">No Invoices Found</h4>
              <p className="text-slate-400 text-xs font-medium max-w-xs mb-5">
                No fee invoices have been issued for this student under the selected session & term.
              </p>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingInvoice(null);
                    setIsModalOpen(true);
                  }}
                  className="bg-[#0F172A] hover:bg-slate-800 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg flex items-center gap-2"
                >
                  <Plus size={15} /> Create First Invoice
                </button>
              )}
            </div>
          ) : (
            invoices.map((inv) => (
              <div
                key={inv.id}
                className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all space-y-4 relative"
              >
                {/* Header info */}
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        {inv.academic_session} • Term {inv.term}
                      </span>
                      {getStatusBadge(inv.status, inv.remaining_balance)}
                    </div>
                    <p className="text-xs font-bold text-slate-600">
                      Due Date: <span className="text-slate-800">{inv.due_date || "N/A"}</span>
                    </p>
                  </div>

                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingInvoice(inv);
                          setIsModalOpen(true);
                        }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                        title="Edit Invoice"
                      >
                        <Edit3 size={16} />
                      </button>
                      {(() => {
                        const isPaidOrPartial = Number(inv.amount_paid || 0) > 0;
                        return (
                          <button
                            type="button"
                            disabled={isPaidOrPartial}
                            onClick={() => handleDeleteInvoice(inv)}
                            className={`p-2 rounded-xl transition-all ${
                              isPaidOrPartial
                                ? "text-slate-300 cursor-not-allowed opacity-30 hover:bg-transparent"
                                : "text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer"
                            }`}
                            title={
                              isPaidOrPartial
                                ? `Cannot delete invoice: ₦${Number(inv.amount_paid).toLocaleString()} already paid`
                                : "Delete Invoice"
                            }
                          >
                            <Trash2 size={16} />
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Amount breakdown */}
                <div className="bg-slate-50 rounded-2xl p-3.5 flex items-center justify-between gap-2 border border-slate-100">
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                      Total Amount
                    </span>
                    <span className="text-base font-black text-slate-900">
                      ₦{Number(inv.total_amount || 0).toLocaleString()}
                    </span>
                  </div>

                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                      Amount Paid
                    </span>
                    <span className="text-base font-black text-emerald-600">
                      ₦{Number(inv.amount_paid || 0).toLocaleString()}
                    </span>
                  </div>

                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                      Remaining
                    </span>
                    <span className="text-base font-black text-rose-600">
                      ₦{Number(inv.remaining_balance ?? inv.total_amount).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Stationery / Line Items */}
                {((inv.stationary_items && inv.stationary_items.length > 0) ||
                  (inv.line_items && inv.line_items.length > 0)) && (
                  <div className="space-y-1.5 pt-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      Stationery Items Breakdown
                    </span>
                    <div className="divide-y divide-slate-100 bg-slate-50/50 rounded-xl px-3 py-1 border border-slate-100 text-xs">
                      {(inv.stationary_items || inv.line_items)?.map((item: any, idx: number) => {
                        const itemName =
                          item.stationary?.name || item.description || `Stationery Item #${idx + 1}`;
                        const unitPrice = item.unit_price || item.stationary?.unit_price || 0;
                        const qty = item.quantity || 1;
                        const grossTotal = qty * unitPrice;
                        const discPercent = item.discount_percentage ? Number(item.discount_percentage) : 0;
                        const total = item.amount !== undefined ? item.amount : grossTotal - (grossTotal * discPercent) / 100;
                        return (
                          <div key={idx} className="py-2 flex justify-between items-center">
                            <div>
                              <span className="font-semibold text-slate-700">
                                {itemName} ({qty}x)
                              </span>
                              {discPercent > 0 && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                                  -{discPercent}% off
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-slate-900 block">
                                ₦{total.toLocaleString()}
                              </span>
                              {discPercent > 0 && (
                                <span className="line-through text-[10px] text-slate-400">
                                  ₦{grossTotal.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Overall / Sibling Discount Badge if present */}
                {(Number(inv.overall_discount_percentage) > 0 || Number(inv.total_discount_amount) > 0) && (
                  <div className="bg-emerald-50 text-emerald-800 border border-emerald-200/80 rounded-xl px-3 py-2 text-xs font-bold flex items-center justify-between">
                    <span>
                      🎉 Discount Applied {Number(inv.overall_discount_percentage) > 0 ? `(${inv.overall_discount_percentage}% Sibling/Student)` : ""}:
                    </span>
                    <span className="font-black text-emerald-900">
                      Savings: -₦{(Number(inv.total_discount_amount) || Number(inv.overall_discount_amount) || 0).toLocaleString()}
                    </span>
                  </div>
                )}

                {inv.notes && (
                  <p className="text-xs text-slate-400 italic">
                    Note: {inv.notes}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Invoice Modal for Create / Edit */}
      {isModalOpen && (
        <InvoiceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={fetchInvoices}
          preselectedStudent={{ id: studentId, name: studentName }}
          initialData={editingInvoice}
        />
      )}
    </div>
  );
};
